import { F } from '@/utils/intl';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Input } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import {
  extractTransKeys,
  substituteTransFilters,
} from './EmailBodyEditor/insertion';
import { humanizeTransKey } from './EmailBodyEditor/labels';
import TranslationDisplay from './TranslationDisplay';
import TranslationHint from './TranslationHint';

import styles from './ResolvedTranslationHint.less';

interface ResolvedTranslationHintProps {
  /** The field's raw value (may contain `{{ "key"|trans }}` expressions). */
  source?: unknown;
  /** Partner translations for the previewed language (`group.key` → text). */
  translations: Record<string, string>;
  /** Persists one key's wording partner-wide; resolves true on success. */
  onSave: (key: string, value: string) => Promise<boolean>;
}

/**
 * What a **mixed** field resolves to — one whose value is part words, part
 * `{{ 'key'|trans }}`, so no single binding owns it and the input keeps showing the raw
 * value. Rendered in the same `TranslationDisplay` a wholly bound field uses (wording,
 * keys beneath) and actionable: "Edit translation" turns it into inline inputs, one per
 * key, that persist partner-wide for the previewed language.
 *
 * Renders nothing when the field uses no translations at all, and falls back to the
 * `TranslationHint` strip when no key has a wording yet — there is nothing to display.
 */
const ResolvedTranslationHint: React.FC<ResolvedTranslationHintProps> = ({
  source,
  translations,
  onSave,
}) => {
  const raw = typeof source === 'string' ? source : '';
  const transKeys = useMemo(() => extractTransKeys(raw), [raw]);

  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const startEditing = useCallback(() => {
    const initial: Record<string, string> = {};
    for (const key of transKeys) {
      initial[key] = translations[key] ?? '';
    }
    setDrafts(initial);
    setEditing(true);
  }, [transKeys, translations]);

  const handleSave = useCallback(
    (key: string) => {
      setSavingKey(key);
      onSave(key, drafts[key] ?? '')
        .then((saved) => {
          // Single-key fields close after a successful save; multi-key
          // fields stay open so the remaining keys can be edited too.
          if (saved && transKeys.length === 1) setEditing(false);
        })
        .finally(() => setSavingKey(null));
    },
    [onSave, drafts, transKeys.length],
  );

  if (transKeys.length === 0) return null;

  const resolved = substituteTransFilters(raw, translations);
  const hasResolution = resolved !== raw;

  if (!editing) {
    /**
     * Resolved: the same block a wholly bound field shows — the wording itself, with
     * the keys it came from beneath it. It used to be a sentence
     * (*"Recipients will see: …"*) with no key, which said the same thing in a second
     * shape and buried the wording inside a label.
     */
    if (hasResolution) {
      return (
        <TranslationDisplay
          variant="standalone"
          text={resolved}
          keys={transKeys}
          onClick={startEditing}
          action={
            <ConfigProvider componentDisabled={false}>
              <Button
                className={styles.editAction}
                type="link"
                size="small"
                onClick={startEditing}
              >
                <F
                  id="pages.emailCenter.templates.resolvedHint.edit"
                  defaultMessage="Edit translation"
                />
              </Button>
            </ConfigProvider>
          }
        />
      );
    }

    return (
      <TranslationHint
        actionLabel={
          <F
            id="pages.emailCenter.templates.resolvedHint.add"
            defaultMessage="Add translation"
          />
        }
        onAction={startEditing}
      >
        <F
          id="pages.emailCenter.templates.resolvedHint.missing"
          defaultMessage="No saved text for this language yet."
        />
      </TranslationHint>
    );
  }

  return (
    <TranslationHint>
      {transKeys.map((key) => (
        <span key={key} className={styles.editRow}>
          {transKeys.length > 1 && (
            <span className={styles.editLabel}>
              {humanizeTransKey(key).label}
            </span>
          )}
          <Input
            size="small"
            value={drafts[key] ?? ''}
            onChange={(event) =>
              setDrafts((current) => ({
                ...current,
                [key]: event.target.value,
              }))
            }
            onPressEnter={() => handleSave(key)}
          />
          <Button
            size="small"
            type="primary"
            ghost
            icon={<CheckOutlined />}
            loading={savingKey === key}
            onClick={() => handleSave(key)}
          />
        </span>
      ))}
      <Button
        size="small"
        type="text"
        icon={<CloseOutlined />}
        onClick={() => setEditing(false)}
      />
    </TranslationHint>
  );
};

export default React.memo(ResolvedTranslationHint);
